import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const GREEN = [22, 163, 106]; // #16a34a
const DARK = [17, 24, 39]; // gray-900
const GRAY = [107, 114, 128]; // gray-500
const LIGHT_GREEN = [240, 253, 244]; // green-50

/**
 * Creates a branded PDF document with header and footer
 * @param {string} title - Report title
 * @param {string} subtitle - Report subtitle
 * @param {object} branding - {logoUrl, companyName} from BrandingContext
 * @returns {jsPDF} doc instance
 */
export const createBrandedPDF = (title, subtitle, branding = {}) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();

  // Header bar
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, pageWidth, 28, 'F');

  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(branding.companyName || '1DATA', 14, 14);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle || 'Report', 14, 22);

  // Date on right
  doc.setFontSize(9);
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth - 14, 14, { align: 'right' });
  doc.text(title, pageWidth - 14, 22, { align: 'right' });

  // Reset text color
  doc.setTextColor(...DARK);

  // Add footer function
  const addFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFillColor(245, 245, 245);
      doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text('Powered by 1BUY.AI', 14, pageHeight - 5);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 5, { align: 'right' });
    }
  };

  doc._addFooter = addFooter;
  doc._startY = 36; // Content start position

  return doc;
};

/**
 * Adds a section heading
 */
export const addSectionTitle = (doc, title, y) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - 30) { doc.addPage(); y = 20; }
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(title, 14, y);
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.5);
  doc.line(14, y + 2, 80, y + 2);
  return y + 10;
};

/**
 * Adds key-value metric cards in a row
 */
export const addMetricCards = (doc, metrics, y) => {
  const cardWidth = 42;
  const startX = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxCols = Math.min(metrics.length, 4);
  const gap = (pageWidth - 28 - cardWidth * maxCols) / Math.max(maxCols - 1, 1);

  metrics.forEach((m, i) => {
    const x = startX + i * (cardWidth + gap);
    // Card background
    doc.setFillColor(...LIGHT_GREEN);
    doc.roundedRect(x, y, cardWidth, 20, 2, 2, 'F');
    // Label
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    doc.text(m.label, x + 3, y + 6);
    // Value — auto-shrink if text is too wide for the card
    const valStr = String(m.value);
    let valSize = 14;
    doc.setFontSize(valSize);
    while (valSize > 7 && doc.getTextWidth(valStr) > cardWidth - 6) {
      valSize -= 1;
      doc.setFontSize(valSize);
    }
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GREEN);
    doc.text(valStr, x + 3, y + 16);
  });

  return y + 28;
};

/**
 * Adds a table using jspdf-autotable
 */
export const addTable = (doc, headers, rows, y, options = {}) => {
  const colCount = headers.length;
  const fontSize = colCount > 8 ? 6 : colCount > 6 ? 7 : 8;
  const cellPadding = colCount > 8 ? 1.5 : colCount > 6 ? 2 : 3;

  // Check if new page needed
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - 40) {
    doc.addPage();
    y = 20;
  }

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: rows,
    margin: { left: 14, right: 14 },
    headStyles: {
      fillColor: GREEN,
      textColor: [255, 255, 255],
      fontSize: fontSize,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: fontSize,
      textColor: DARK,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    styles: {
      cellPadding: cellPadding,
      lineWidth: 0.1,
      lineColor: [229, 231, 235],
      overflow: 'linebreak',
    },
    columnStyles: colCount > 8 ? { 0: { cellWidth: 28 } } : {},
    ...options,
  });

  return doc.lastAutoTable.finalY + 8;
};

/**
 * Adds a simple text paragraph
 */
export const addText = (doc, text, y, options = {}) => {
  doc.setFontSize(options.fontSize || 9);
  doc.setFont('helvetica', options.bold ? 'bold' : 'normal');
  doc.setTextColor(...(options.color || DARK));
  const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - 28);
  doc.text(lines, 14, y);
  return y + lines.length * (options.fontSize || 9) * 0.5 + 4;
};

/**
 * Finalize and download the PDF
 */
export const downloadPDF = (doc, filename) => {
  if (doc._addFooter) doc._addFooter();
  doc.save(filename);
};

/**
 * Check if we need a new page
 */
export const checkNewPage = (doc, currentY, neededSpace = 40) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + neededSpace > pageHeight - 20) {
    doc.addPage();
    return 20;
  }
  return currentY;
};

/**
 * Adds a page header for multi-page reports (used on pages 2+)
 */
export const addPageHeader = (doc, title, branding = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, pageWidth, 16, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(branding.companyName || '1DATA', 14, 10);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(title, pageWidth - 14, 10, { align: 'right' });
  doc.setTextColor(...DARK);
  return 24;
};

/**
 * Generates the Executive Board Deck — a multi-page branded PDF
 * pulling insights from Command Center, Your Wins, and Action Priority data
 */
export const generateBoardDeck = (branding = {}, data = {}) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const companyName = branding.companyName || '1DATA';

  // =========== PAGE 1: COVER ===========
  doc.setFillColor(...GREEN);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Big title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(36);
  doc.setFont('helvetica', 'bold');
  doc.text(companyName, pageWidth / 2, 80, { align: 'center' });

  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.text('Executive Board Deck', pageWidth / 2, 95, { align: 'center' });

  doc.setFontSize(11);
  doc.text('Procurement Intelligence & Value Report', pageWidth / 2, 110, { align: 'center' });

  // Date
  doc.setFontSize(12);
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth / 2, 130, { align: 'center' });

  // Bottom branding
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255, 180);
  doc.text('Powered by 1BUY.AI', pageWidth / 2, pageHeight - 20, { align: 'center' });
  doc.setFontSize(7);
  doc.text('Confidential — For Internal Use Only', pageWidth / 2, pageHeight - 14, { align: 'center' });

  // =========== PAGE 2: EXECUTIVE SUMMARY ===========
  doc.addPage();
  let y = addPageHeader(doc, 'Executive Summary', branding);

  y = addSectionTitle(doc, 'Key Performance Indicators', y);
  const kpis = data.kpis || {};
  y = addMetricCards(doc, [
    { label: 'Total Value Delivered', value: kpis.totalSavings || '$847,500' },
    { label: 'BOMs Optimized', value: kpis.optimizedBOMs || '18/24' },
    { label: 'Risk Index', value: kpis.riskIndex || '34/100' },
    { label: 'Actions Completed', value: kpis.actionsCompleted || '22' },
  ], y);

  y = addSectionTitle(doc, 'Savings by Category', y);
  const savingsCats = data.savingsCategories || [
    ['Alternate Parts', '$285,000', '34%'],
    ['Negotiation', '$225,000', '27%'],
    ['Volume Consolidation', '$168,000', '20%'],
    ['Distributor Switch', '$102,000', '12%'],
    ['Risk Avoidance', '$67,500', '8%'],
  ];
  y = addTable(doc, ['Category', 'Amount', '% of Total'], savingsCats, y);

  y = checkNewPage(doc, y, 60);
  y = addSectionTitle(doc, 'Savings Trend (6 Months)', y);
  const trendRows = data.savingsTrend || [
    ['Sep', '$42,000'], ['Oct', '$58,000'], ['Nov', '$72,000'],
    ['Dec', '$95,000'], ['Jan', '$118,000'], ['Feb', '$145,000'],
  ];
  y = addTable(doc, ['Month', 'Savings'], trendRows, y);

  // =========== PAGE 3: YOUR WINS ===========
  doc.addPage();
  y = addPageHeader(doc, 'Value Engine — Your Wins', branding);

  y = addSectionTitle(doc, 'Highlights', y);
  const winStats = data.winStats || {};
  y = addMetricCards(doc, [
    { label: 'Total Value', value: winStats.totalValue || '$847,500' },
    { label: 'Risks Avoided', value: winStats.risksAvoided || '12' },
    { label: 'Negotiations Won', value: winStats.negotiationsWon || '34' },
    { label: 'Alternates Adopted', value: winStats.alternates || '18' },
  ], y);

  y = addSectionTitle(doc, 'Recent Achievements', y);
  const wins = data.recentWins || [
    ['Renegotiated MLCC pricing with Murata', 'Negotiation', '$28,500', 'High'],
    ['Switched STM32F4 to FFF alternate', 'Alternate', '$14,200', 'High'],
    ['Avoided obsolescence on MT48LC memory', 'Risk Mitigation', '$45,000', 'Critical'],
    ['Consolidated capacitor orders', 'Volume', '$8,700', 'Medium'],
    ['Early buy before connector price increase', 'Prediction', '$12,300', 'Medium'],
  ];
  y = addTable(doc, ['Achievement', 'Category', 'Value', 'Impact'], wins, y);

  // =========== PAGE 4: ACTION PRIORITY ===========
  doc.addPage();
  y = addPageHeader(doc, 'Action Priority Dashboard', branding);

  y = addSectionTitle(doc, 'Top Risks Requiring Attention', y);
  const risks = data.topRisks || [
    ['STM32F407 supply shortage', 'Critical', '$145,000', '8 BOMs', 'Qualify alternate MCU'],
    ['MLCC price increase forecast', 'High', '$89,000', '12 BOMs', 'Pre-buy at current price'],
    ['MT48LC memory obsolescence', 'High', '$67,000', '3 BOMs', 'Migrate to DDR4 equivalent'],
  ];
  y = addTable(doc, ['Risk', 'Severity', 'Impact', 'Affected', 'Action'], risks, y);

  y = checkNewPage(doc, y, 60);
  y = addSectionTitle(doc, 'Top Negotiation Opportunities', y);
  const negotiations = data.topNegotiations || [
    ['GRM188R71H104KA93D', 'Murata via DigiKey', '$0.028', '$0.018', '64%', '$12,500/yr'],
    ['ESP32-WROOM-32E', 'Espressif via Arrow', '$3.20', '$2.35', '73%', '$8,500/yr'],
    ['TPS54331DR', 'TI via Mouser', '$1.65', '$1.20', '73%', '$4,500/yr'],
  ];
  y = addTable(doc, ['MPN', 'Supplier', 'Your Price', 'Market Avg', 'Efficiency', 'Savings'], negotiations, y);

  y = checkNewPage(doc, y, 60);
  y = addSectionTitle(doc, 'High-Impact Alternate Parts', y);
  const alternates = data.topAlternates || [
    ['STM32F407VGT6', 'GD32F407VGT6', 'FFF', '98%', '$14,200/yr'],
    ['MT48LC16M16A2', 'IS42S16320D-7TLI', 'Pin-to-Pin', '95%', '$8,800/yr'],
    ['LAN8720A-CP-TR', 'KSZ8081RNAIA', 'FFF', '96%', '$5,200/yr'],
  ];
  y = addTable(doc, ['Original MPN', 'Alternate', 'Type', 'Match', 'Savings'], alternates, y);

  // =========== PAGE 5: SUPPLIER PERFORMANCE ===========
  doc.addPage();
  y = addPageHeader(doc, 'Supplier Performance', branding);

  y = addSectionTitle(doc, 'Supplier Rankings', y);
  const suppliers = data.supplierPerformance || [
    ['DigiKey', 'Platinum', '92', '96%', '94%', '88%', '$125,000'],
    ['Mouser', 'Gold', '88', '94%', '92%', '85%', '$98,000'],
    ['Arrow', 'Gold', '85', '90%', '88%', '82%', '$76,000'],
    ['Avnet', 'Silver', '82', '88%', '85%', '80%', '$65,000'],
    ['Newark', 'Silver', '78', '85%', '82%', '78%', '$42,000'],
  ];
  y = addTable(doc, ['Supplier', 'Tier', 'Score', 'On-Time', 'Quality', 'Price', 'Savings'], suppliers, y);

  y = checkNewPage(doc, y, 40);
  y = addSectionTitle(doc, 'Risk Distribution', y);
  const riskDist = data.riskDistribution || [
    ['Low', '68', '68%'],
    ['Medium', '22', '22%'],
    ['High', '7', '7%'],
    ['Critical', '3', '3%'],
  ];
  y = addTable(doc, ['Level', 'Count', 'Percentage'], riskDist, y);

  // Add footer to all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    if (i === 1) continue; // Skip cover page footer
    doc.setFillColor(245, 245, 245);
    doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text('Powered by 1BUY.AI  |  Confidential', 14, pageHeight - 5);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 5, { align: 'right' });
  }

  doc.save(`${companyName.replace(/\s+/g, '-')}-Board-Deck-${new Date().toISOString().split('T')[0]}.pdf`);
};
